/**
 * TransportPlanningPage — Transportplanung (Jun-2026, User-Wunsch).
 *
 * Eigener Bereich (Menü „Transport"): alle Transportaufträge des Standorts mit
 * Status-Filter, Termin-/Transporteur-Bearbeitung und Statuswechsel. Aufträge
 * aus verkauften Verträgen (Auslieferung an Kunden, source="sale") erscheinen
 * automatisch und sind hier einplanbar.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Truck, MapPin, Calendar, User, Trash2, Tag, CalendarDays, List, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import OrgBranchSwitcher from "../components/org/OrgBranchSwitcher";
import { previewPdfResponse } from "../lib/pdfPreview";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const STATUS = {
    open: { label: "Offen", color: "bg-gray-100 text-gray-700" },
    planned: { label: "Geplant", color: "bg-amber-100 text-amber-800" },
    in_transit: { label: "Unterwegs", color: "bg-blue-100 text-blue-800" },
    done: { label: "Erledigt", color: "bg-green-100 text-green-800" },
    cancelled: { label: "Storniert", color: "bg-red-100 text-red-700" },
};
const ORDER = ["open", "planned", "in_transit", "done", "cancelled"];
const veh = (v) => [v?.brand, v?.model, v?.license_plate].filter(Boolean).join(" ") || v?.title || "—";

// Jul-2026 (User-Wunsch) — Standort-Filter: matcht Transportauftrag gegen einen
// Händler-Standort über Von-/Nach-Adresse (Code, Name, Straße oder Stadt).
const siteMatch = (order, site) => {
    if (!site) return true;
    const hay = `${order.from_location || ""} ${order.to_location || ""} ${order.customer_address || ""}`.toLowerCase();
    const needles = [site.code, site.name, site.street, site.city].filter(Boolean).map((s) => String(s).toLowerCase());
    return needles.some((n) => n && hay.includes(n));
};

// Jul-2026 (User-Wunsch) — Fällig-/Überfällig-Ampel für offene Transporte.
const _daysSince = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (isNaN(d)) return null;
    return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
const transportUrgency = (t) => {
    if (!t || t.status === "done" || t.status === "cancelled") return null;
    if (t.scheduled_date) {
        const sd = new Date(t.scheduled_date);
        if (!isNaN(sd) && sd.getTime() < Date.now() - 86400000) return "red";
    }
    const age = _daysSince(t.created_at);
    if (age == null) return "green";
    if (age > 7) return "red";
    if (age > 3) return "amber";
    return "green";
};
const URG_DOT = { red: "bg-red-500", amber: "bg-amber-500", green: "bg-emerald-500" };


// --- Datums-Helfer für die Wochenansicht (Montag-Start) ---
const isoDay = (d) => {
    const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return z.toISOString().slice(0, 10);
};
const mondayOf = (d) => {
    const x = new Date(d);
    const day = (x.getDay() + 6) % 7; // Mo=0 … So=6
    x.setDate(x.getDate() - day);
    x.setHours(0, 0, 0, 0);
    return x;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const DAY_NAMES = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

export default function TransportPlanningPage() {
    const { user } = useAuth();
    const [orgBranch, setOrgBranch] = useState("");
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [filter, setFilter] = useState("");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("list"); // list | week
    const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
    const [sites, setSites] = useState([]);
    const [siteFilter, setSiteFilter] = useState("all");

    useEffect(() => {
        api.get("/users/me/dealer-sites").then((r) => setSites(r.data?.items || [])).catch(() => {});
    }, []);

    const load = useCallback(async () => {        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filter) params.set("status", filter);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/transport-orders?${params}`);
            setItems(data.items || []);
            setCounts(data.counts || {});
        } catch {
            toast.error("Transportaufträge konnten nicht geladen werden.");
        } finally { setLoading(false); }
    }, [filter, q]);

    useEffect(() => { load(); }, [load, orgBranch]);

    const patch = async (id, body) => {
        try {
            await api.patch(`/transport-orders/${id}`, body);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen."); }
    };
    const remove = async (id) => {
        if (!window.confirm("Transportauftrag wirklich löschen?")) return;
        try { await api.delete(`/transport-orders/${id}`); toast.success("Gelöscht."); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen."); }
    };
    const bundle = async (ids) => {
        try {
            const { data } = await api.post("/transport-orders/bundle", { ids });
            toast.success(`Sammelfahrt erstellt (${data.count} Fahrzeuge).`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Bündeln fehlgeschlagen."); }
    };
    const unbundle = async (gid) => {
        try { await api.post(`/transport-orders/group/${gid}/unbundle`); toast.success("Sammelfahrt aufgelöst."); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Auflösen fehlgeschlagen."); }
    };
    const downloadLadeplan = async (gid) => {
        try {
            const res = await api.get(`/transport-orders/group/${gid}/ladeplan.pdf`, { responseType: "blob" });
            previewPdfResponse(res, `Ladeplan_${gid.slice(0, 8)}.pdf`);
        } catch (e) { toast.error(e.response?.data?.detail || "Ladeplan fehlgeschlagen."); }
    };

    const activeSite = siteFilter === "all" ? null : sites.find((s) => (s.code || s.id) === siteFilter);
    const visibleItems = activeSite ? items.filter((t) => siteMatch(t, activeSite)) : items;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-[#111827] text-white">                <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dashboard" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="back-to-dashboard">← Dashboard</Link>
                    <div className="flex items-end justify-between gap-4 mt-4 flex-wrap">
                        <div>
                            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight flex items-center gap-3" data-testid="transport-page-title">
                                <Truck className="w-9 h-9 text-[#FFCC00]" /> Transportplanung
                            </h1>
                            <p className="text-sm text-gray-400 mt-2 truncate max-w-full">
                                Fahrzeug-Transporte planen & verfolgen. Auslieferungen aus verkauften Verträgen erscheinen hier automatisch.
                            </p>
                        </div>
                        <OrgBranchSwitcher user={user} value={orgBranch} onChange={setOrgBranch} />
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 py-6 max-w-6xl">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                    <button type="button" onClick={() => setFilter("")}
                        className={`px-3 py-1.5 rounded-sm text-xs font-bold border ${filter === "" ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                        data-testid="transport-filter-all">Alle <span className="opacity-60">({ORDER.reduce((s, k) => s + (counts[k] || 0), 0)})</span></button>
                    {ORDER.map((s) => (
                        <button key={s} type="button" onClick={() => setFilter(s)}
                            className={`px-3 py-1.5 rounded-sm text-xs font-bold border ${filter === s ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                            data-testid={`transport-filter-${s}`}>
                            {STATUS[s].label} <span className="opacity-60">({counts[s] || 0})</span>
                        </button>
                    ))}
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suche (Fahrzeug, Kunde, Ort…)"
                        className="h-9 w-64 rounded-sm ml-auto" data-testid="transport-search" />
                    <div className="flex items-center rounded-sm border border-gray-300 overflow-hidden">
                        <button type="button" onClick={() => setView("list")}
                            className={`px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 ${view === "list" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="transport-view-list"><List className="w-3.5 h-3.5" /> Liste</button>
                        <button type="button" onClick={() => setView("week")}
                            className={`px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 ${view === "week" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
                            data-testid="transport-view-week"><CalendarDays className="w-3.5 h-3.5" /> Woche</button>
                    </div>
                </div>

                {sites.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="transport-site-filter">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500 inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Standort:</span>
                        <select
                            value={siteFilter}
                            onChange={(e) => setSiteFilter(e.target.value)}
                            className="h-9 min-w-[220px] max-w-full rounded-sm border-2 border-gray-300 bg-white px-3 text-sm font-bold text-[#111827] focus:border-[#111827] focus:outline-none"
                            data-testid="transport-site-select"
                        >
                            <option value="all">Alle Standorte ({sites.length})</option>
                            {sites.map((s) => {
                                const key = s.code || s.id;
                                const label = `${s.code ? `${s.code} · ` : ""}${s.name || ""}${s.is_default ? " ★" : ""}`.trim();
                                return <option key={key} value={key}>{label}</option>;
                            })}
                        </select>
                    </div>
                )}

                {view === "week" ? (
                    <WeekView items={visibleItems} weekStart={weekStart} setWeekStart={setWeekStart} patch={patch} bundle={bundle} unbundle={unbundle} downloadLadeplan={downloadLadeplan} />
                ) : loading ? (
                    <div className="text-sm text-gray-400 py-12 text-center">Lade…</div>
                ) : visibleItems.length === 0 ? (
                    <div className="text-sm text-gray-400 py-12 text-center border border-dashed border-gray-300 rounded-sm" data-testid="transport-empty">
                        {activeSite ? "Keine Transportaufträge für diesen Standort." : "Keine Transportaufträge. Erstellen Sie einen über „Transportauftrag“ in der Disposition."}
                    </div>
                ) : (
                    <ul className="space-y-2" data-testid="transport-list">
                        {visibleItems.map((t) => (
                            <li key={t.id} className="bg-white border border-gray-200 rounded-sm p-3" data-testid={`transport-item-${t.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-bold">{veh(t.vehicle)}</span>
                                            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${STATUS[t.status]?.color}`}>{STATUS[t.status]?.label}</span>
                                            {transportUrgency(t) && (
                                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${transportUrgency(t) === "red" ? "text-red-700" : transportUrgency(t) === "amber" ? "text-amber-700" : "text-emerald-700"}`} title={transportUrgency(t) === "red" ? "Überfällig" : transportUrgency(t) === "amber" ? "Fällig bald" : "Im Plan"} data-testid={`transport-urgency-${t.id}`}>
                                                    <span className={`w-2 h-2 rounded-full ${URG_DOT[transportUrgency(t)]}`} />
                                                    {_daysSince(t.created_at) != null ? `${_daysSince(t.created_at)} T.` : ""}
                                                </span>
                                            )}
                                            {t.source === "sale" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-700">aus Verkauf</span>}
                                            {t.vehicle?.vin && <span className="text-[11px] text-gray-400 inline-flex items-center gap-1"><Tag className="w-3 h-3" />{t.vehicle.vin}</span>}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                                            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.from_location || "—"} → {t.to_location || t.customer_address || "—"}</span>
                                            {t.customer_name && <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {t.customer_name}</span>}
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => remove(t.id)} className="text-gray-400 hover:text-red-600 p-1" data-testid={`transport-delete-${t.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> Datum</label>
                                        <Input type="date" value={t.scheduled_date || ""} onChange={(e) => patch(t.id, { scheduled_date: e.target.value })} className="h-8 text-xs rounded-sm mt-0.5" data-testid={`transport-date-${t.id}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Zeitfenster</label>
                                        <Input value={t.time_window || ""} onBlur={(e) => patch(t.id, { time_window: e.target.value })} onChange={(e) => setItems((p) => p.map((x) => x.id === t.id ? { ...x, time_window: e.target.value } : x))} className="h-8 text-xs rounded-sm mt-0.5" data-testid={`transport-window-${t.id}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Transporteur</label>
                                        <Input value={t.carrier || ""} onBlur={(e) => patch(t.id, { carrier: e.target.value })} onChange={(e) => setItems((p) => p.map((x) => x.id === t.id ? { ...x, carrier: e.target.value } : x))} className="h-8 text-xs rounded-sm mt-0.5" data-testid={`transport-carrier-${t.id}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Fahrer</label>
                                        <Input value={t.driver || ""} onBlur={(e) => patch(t.id, { driver: e.target.value })} onChange={(e) => setItems((p) => p.map((x) => x.id === t.id ? { ...x, driver: e.target.value } : x))} className="h-8 text-xs rounded-sm mt-0.5" data-testid={`transport-driver-${t.id}`} />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Status</label>
                                        <Select value={t.status} onValueChange={(v) => patch(t.id, { status: v })}>
                                            <SelectTrigger className="h-8 text-xs rounded-sm mt-0.5" data-testid={`transport-status-${t.id}`}><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {t.note && <p className="text-[11px] text-gray-400 mt-2 italic">{t.note}</p>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function WeekView({ items, weekStart, setWeekStart, patch, bundle, unbundle, downloadLadeplan }) {
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
    const todayIso = isoDay(new Date());
    // Items je Tag + „ohne Termin"; Bündelungs-Erkennung (gleiches Ziel am selben Tag).
    const byDay = {};
    const unscheduled = [];
    for (const t of items) {
        if (t.scheduled_date) (byDay[t.scheduled_date] = byDay[t.scheduled_date] || []).push(t);
        else unscheduled.push(t);
    }
    const destCounts = (list) => {
        const m = {};
        list.forEach((t) => { const k = (t.to_location || t.customer_address || "").trim().toLowerCase(); if (k) m[k] = (m[k] || 0) + 1; });
        return m;
    };

    return (
        <div data-testid="transport-week">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setWeekStart((w) => addDays(w, -7))} className="h-8" data-testid="transport-week-prev"><ChevronLeft className="w-4 h-4" /></Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setWeekStart(mondayOf(new Date()))} className="h-8 text-xs" data-testid="transport-week-today">Heute</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => setWeekStart((w) => addDays(w, 7))} className="h-8" data-testid="transport-week-next"><ChevronRight className="w-4 h-4" /></Button>
                </div>
                <div className="text-sm font-bold text-gray-700">
                    {weekStart.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – {addDays(weekStart, 6).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
            </div>

            {unscheduled.length > 0 && (
                <div className="mb-3 border border-dashed border-amber-300 bg-amber-50 rounded-sm p-2" data-testid="transport-week-unscheduled">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Ohne Termin ({unscheduled.length}) — Datum zuweisen, um einzuplanen</div>
                    <div className="flex gap-2 flex-wrap">
                        {unscheduled.map((t) => (
                            <div key={t.id} className="bg-white border border-amber-200 rounded-sm px-2 py-1 text-xs flex items-center gap-2" data-testid={`transport-week-unscheduled-${t.id}`}>
                                <span className="font-medium">{veh(t.vehicle)}</span>
                                <Input type="date" onChange={(e) => patch(t.id, { scheduled_date: e.target.value, status: t.status === "open" ? "planned" : t.status })} className="h-7 text-[11px] rounded-sm w-32" data-testid={`transport-week-assign-${t.id}`} />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                {days.map((d, i) => {
                    const di = isoDay(d);
                    const list = (byDay[di] || []);
                    const counts = destCounts(list);
                    // Cluster (gleiches Ziel) für Bündelung/Sammelfahrt
                    const clusters = {};
                    list.forEach((t) => {
                        const k = (t.to_location || t.customer_address || "").trim().toLowerCase();
                        if (!k) return;
                        (clusters[k] = clusters[k] || { dest: t.to_location || t.customer_address, list: [] }).list.push(t);
                    });
                    return (
                        <div key={di} className={`border rounded-sm min-h-[120px] ${di === todayIso ? "border-[#FFCC00] ring-1 ring-[#FFCC00]" : "border-gray-200"}`} data-testid={`transport-week-day-${di}`}>
                            <div className={`px-2 py-1 text-xs font-bold flex items-center justify-between ${di === todayIso ? "bg-[#FFCC00] text-[#111827]" : "bg-gray-50 text-gray-600"}`}>
                                <span>{DAY_NAMES[i]} {d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}</span>
                                {list.length > 0 && <span className="opacity-70">{list.length}</span>}
                            </div>
                            <div className="p-1.5 space-y-1.5">
                                {list.map((t) => {
                                    const key = (t.to_location || t.customer_address || "").trim().toLowerCase();
                                    const bundle2 = key && counts[key] > 1;
                                    return (
                                        <div key={t.id} className={`rounded-sm border bg-white p-1.5 text-[11px] ${t.group_id ? "border-l-4 border-l-indigo-600 border-gray-200" : bundle2 ? "border-l-4 border-l-indigo-300 border-gray-200" : "border-gray-200"}`} data-testid={`transport-week-item-${t.id}`} title={t.group_id ? "Teil einer Sammelfahrt" : bundle2 ? "Gleiches Ziel am selben Tag — bündelbar" : ""}>
                                            <div className="font-medium truncate">{veh(t.vehicle)}</div>
                                            <div className="text-gray-500 truncate flex items-center gap-1"><MapPin className="w-2.5 h-2.5 shrink-0" />{t.to_location || t.customer_address || "—"}</div>
                                            <div className="flex items-center justify-between mt-1">
                                                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-sm ${STATUS[t.status]?.color}`}>{STATUS[t.status]?.label}</span>
                                                {t.group_id ? <span className="text-[9px] font-bold text-indigo-700">Sammelfahrt</span> : bundle2 && <span className="text-[9px] font-bold text-indigo-500">bündelbar</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {/* Bündelungs-/Sammelfahrt-Aktionen je Ziel */}
                                {Object.values(clusters).filter((cl) => cl.list.length > 1).map((cl) => {
                                    const gids = [...new Set(cl.list.map((t) => t.group_id).filter(Boolean))];
                                    const allSameGroup = gids.length === 1 && cl.list.every((t) => t.group_id === gids[0]);
                                    const dkey = cl.dest.replace(/\s+/g, "-").toLowerCase();
                                    return (
                                        <div key={dkey} className="border border-indigo-200 bg-indigo-50 rounded-sm p-1 mt-1" data-testid={`transport-week-cluster-${di}-${dkey}`}>
                                            <div className="text-[9px] font-bold text-indigo-700 truncate">→ {cl.dest} ({cl.list.length})</div>
                                            {allSameGroup ? (
                                                <div className="flex gap-1 mt-1">
                                                    <button type="button" onClick={() => downloadLadeplan(gids[0])} className="flex-1 text-[9px] font-bold bg-indigo-600 text-white rounded-sm py-1 hover:bg-indigo-700" data-testid={`transport-week-ladeplan-${di}-${dkey}`}>Ladeplan</button>
                                                    <button type="button" onClick={() => unbundle(gids[0])} className="text-[9px] font-bold border border-indigo-300 text-indigo-700 rounded-sm px-1.5 hover:bg-white" data-testid={`transport-week-unbundle-${di}-${dkey}`}>Auflösen</button>
                                                </div>
                                            ) : (
                                                <button type="button" onClick={() => bundle(cl.list.map((t) => t.id))} className="w-full text-[9px] font-bold bg-indigo-600 text-white rounded-sm py-1 mt-1 hover:bg-indigo-700" data-testid={`transport-week-bundle-${di}-${dkey}`}>
                                                    🔗 Zu Sammelfahrt bündeln
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
